import type { ComponentType, SVGProps } from 'react'
import { useTranslation } from 'react-i18next'
import { useCollectionDot } from '@/features/collection/hooks/use-collection-dot'
import { useCurrentFlowDay } from '@/features/day-flow/hooks/use-current-flow-day'
import { CollectionIcon } from '@/shared/components/icons/collection'
import { DiscoverIcon } from '@/shared/components/icons/discover'
import { MyPlanIcon } from '@/shared/components/icons/my-plan'
import { PersonIcon } from '@/shared/components/icons/person'
import { SunriseIcon } from '@/shared/components/icons/sunrise'
import { useShowsCollection } from '@/shared/hooks/use-shows-collection'

export const NAV_ITEMS: {
  labelKey: string
  desktopLabelKey?: string
  icon: ComponentType<SVGProps<SVGSVGElement>>
  path: string
}[] = [
  {
    labelKey: 'bottom_nav_today',
    desktopLabelKey: 'desktop_nav_plan_web',
    icon: SunriseIcon,
    path: '/plan',
  },
  { labelKey: 'bottom_nav_discover', icon: DiscoverIcon, path: '/discovery' },
  { labelKey: 'bottom_nav_profile', icon: PersonIcon, path: '/profile' },
]

export interface ResolvedNavItem {
  label: string
  desktopLabel: string
  icon: ComponentType<SVGProps<SVGSVGElement>>
  path: string
  /** Cards changed since the queue was last seen. Only the Collection tab ever wears one. */
  hasDot?: boolean
}

// The tab set, resolved for the active plan: flow plans get a dedicated Today
// (day) tab and move the plan map under "My plan"; other plans keep the
// standard tabs.
export function useNavItems(): ResolvedNavItem[] {
  const { t } = useTranslation()
  const { isFlow } = useCurrentFlowDay()

  // The Collection takes Discover's slot rather than adding a fifth tab, for everyone in the arm —
  // a browser that cannot run Compose gets the explanation inside the tab rather than losing it.
  // The plan's allowlist AND the test arm, which is what Android and iOS read — one experiment,
  // not three, and not the allowlist mistaken for it.
  const showsCollection = useShowsCollection()
  // Asked here because this is where the tab's existence is already decided; asking anywhere else
  // would query it for people who have no Collection at all.
  const hasPendingCards = useCollectionDot(showsCollection)
  const discoverSlot: ResolvedNavItem = showsCollection
    ? {
        // Android's key, so one Crowdin string labels the tab on every platform.
        label: t('bottom_nav_collection'),
        desktopLabel: t('bottom_nav_collection'),
        icon: CollectionIcon,
        path: '/collection',
        hasDot: hasPendingCards,
      }
    : {
        label: t('bottom_nav_discover'),
        desktopLabel: t('bottom_nav_discover'),
        icon: DiscoverIcon,
        path: '/discovery',
      }

  if (isFlow) {
    return [
      {
        label: t('bottom_nav_today'),
        desktopLabel: t('bottom_nav_today'),
        icon: SunriseIcon,
        path: '/today',
      },
      {
        label: t('bottom_nav_my_plan'),
        desktopLabel: t('bottom_nav_my_plan'),
        icon: MyPlanIcon,
        path: '/plan',
      },
      discoverSlot,
      {
        label: t('bottom_nav_profile'),
        desktopLabel: t('bottom_nav_profile'),
        icon: PersonIcon,
        path: '/profile',
      },
    ]
  }

  return NAV_ITEMS.map((item) =>
    item.path === '/discovery'
      ? discoverSlot
      : {
          label: t(item.labelKey),
          desktopLabel: t(item.desktopLabelKey ?? item.labelKey),
          icon: item.icon,
          path: item.path,
        },
  )
}
